import styled from "@emotion/styled/macro";
import { Switch, Typography, tokens } from "@sunrun/experience-ui-components";
import { useFlags } from "flagsmith/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useKonami } from "react-konami-code";
import { type AxiosError } from "axios";
import llama1 from "../../assets/images/easterEggImages/llama1.png";
import llama2 from "../../assets/images/easterEggImages/llama2.png";
import llama3 from "../../assets/images/easterEggImages/llama3.png";
import llama4 from "../../assets/images/easterEggImages/llama4.png";
import llama5 from "../../assets/images/easterEggImages/llama5.png";
import llama6 from "../../assets/images/easterEggImages/llama6.png";
import llama7 from "../../assets/images/easterEggImages/llama7.png";
import llama8 from "../../assets/images/easterEggImages/llama8.png";
import llama9 from "../../assets/images/easterEggImages/llama9.png";
import llama10 from "../../assets/images/easterEggImages/llama10.png";
import llama11 from "../../assets/images/easterEggImages/llama11.png";
import llama12 from "../../assets/images/easterEggImages/llama12.png";
import {
  EditOffer,
  EditOfferBundle,
  EditOfferProductAvailability,
  OfferLineCreate,
  OfferLineUpdate,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { useAppSelector } from "store";
import {
  createOfferLines,
  deleteOfferLines,
  updateOfferLines,
} from "services/storeFront";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { BundleRadioButton } from "components/molecules/BundleRadioButton";
import { IncludedProduct } from "components/organisms/IncludedProducts";
import { ProductAccessory } from "components/organisms/ProductAccessory";
import { AdHocAccessoryProduct, AdHocProduct } from "providers/Types";
import {
  STORAGE_ADDON_BUNDLE_ID,
  STORAGE_ADDON_PRODUCT_ID,
  STANDALONE_PRODUCT_ID,
  STANDALONE_EV_CHARGER_PRODUCT_ID,
  STANDALONE_EV_CHARGER_BUNDLE_ID,
} from "constants/bundleAndProductIds";
import { FeatureFlags } from "fixtures/features";
import { hasOnlyIneligibleBundles } from "utils/productAvailability";

type ErrorType = {
  error: Error | AxiosError | undefined;
  attemptedAction: string;
  hasRetry?: boolean;
};

type ProductSelectionOrganismProps = {
  offerId: string;
  productAvailability: EditOfferProductAvailability;
  onBundleLearnMore: () => void;
  bundleIdCache: React.MutableRefObject<string | undefined>;
  expectedLockVersion: React.MutableRefObject<number>;
  offerLinesIdLookup: React.MutableRefObject<EditOffer["lines"]>;
  onIsLoading?: (bool: boolean) => void;
  openProductErrorModal: () => void;
  setOfferLineErrors: React.Dispatch<
    React.SetStateAction<ErrorType | undefined>
  >;
  isOfferRetry: React.MutableRefObject<boolean>;
  currentBundle: React.MutableRefObject<number>;
  previousBundle: React.MutableRefObject<number>;
};

const ProductSelectionOrganism = ({
  offerId,
  productAvailability,
  onBundleLearnMore,
  bundleIdCache,
  expectedLockVersion,
  offerLinesIdLookup,
  onIsLoading,
  openProductErrorModal,
  setOfferLineErrors,
  isOfferRetry,
  currentBundle,
  previousBundle,
}: ProductSelectionOrganismProps) => {
  const getsBundleFromProducts = useCallback(
    <T extends EditOfferBundle>(bundles: T[]) =>
      bundles.map((bundle, index) => {
        if (bundleIdCache && bundleIdCache.current === bundle.id) {
          currentBundle.current = index;

          if (!isOfferRetry.current) {
            previousBundle.current = index;
          }
        }
        return bundle;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productAvailability.bundles]
  );

  const flags = useFlags([FeatureFlags.SPAN_AS_ACCESSORY]);
  const spanAsAccessoryEnabled = flags[FeatureFlags.SPAN_AS_ACCESSORY]?.enabled;

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const [bundles, setBundles] = useState<
    EditOfferProductAvailability["bundles"]
  >(getsBundleFromProducts(productAvailability.bundles));

  const [showIneligibleBundles, setShowIneligibleBundles] = useState<boolean>(
    hasOnlyIneligibleBundles(productAvailability)
  );
  const [ineligibleBundles, setIneligibleBundles] = useState<
    EditOfferProductAvailability["ineligibleBundles"]
  >(getsBundleFromProducts(productAvailability.ineligibleBundles));
  const onlyIneligibleBundles = hasOnlyIneligibleBundles(productAvailability);

  useEffect(() => {
    if (onlyIneligibleBundles) {
      setShowIneligibleBundles(true);
    }
  }, [onlyIneligibleBundles]);

  useEffect(() => {
    const bundles = getsBundleFromProducts(productAvailability.bundles);
    const ineligibleBundles = getsBundleFromProducts(
      productAvailability?.ineligibleBundles
    );
    setBundles(bundles);
    setIneligibleBundles(ineligibleBundles);
  }, [
    productAvailability.bundles,
    productAvailability.ineligibleBundles,
    getsBundleFromProducts,
  ]);

  const [products, setProducts] = useState<Array<AdHocProduct>>(
    (): Array<AdHocProduct> =>
      productAvailability.products.map((product) => ({
        ...product,
        selectedVariantId: offerLinesIdLookup.current.find(
          (line) => line.variant.product.id === product.id
        )?.variant.id,
      }))
  );

  // Any product referenced by a bundle's required or compatible accessories
  // is considered an accessory product, regardless of `isStandaloneProduct`.
  // Standalone products are still treated as accessories so legacy bundles
  // that have not been migrated to the accessory metadata keep working.
  // NOTE: not gated by `spanAsAccessoryEnabled` on purpose. `productAccessories`
  // state below is seeded once from this set; gating here would permanently
  // omit required-only accessories (e.g. SPAN) when Flagsmith hydrates the
  // flag after the initial render. The flag is enforced at render/behavior
  // time instead — the legacy `compatibleAccessories` filter (standalone
  // only) keeps non-standalone products from rendering under the OFF path.
  const accessoryProductIds = useMemo(() => {
    const ids = new Set<string>();
    productAvailability.bundles.forEach((bundle) => {
      bundle.requiredAccessories?.forEach((id) => ids.add(id));
      bundle.compatibleAccessories?.forEach((id) => ids.add(id));
    });
    productAvailability.products.forEach((product) => {
      if (product.isStandaloneProduct) {
        ids.add(product.id);
      }
    });
    return ids;
  }, [productAvailability.bundles, productAvailability.products]);

  const [productAccessories, setProductAccessories] = useState<
    Array<AdHocAccessoryProduct>
  >(() => {
    const seenAccessoryIds = new Set<string>();
    return productAvailability.products
      .filter((product) => accessoryProductIds.has(product.id))
      .filter((product) => {
        if (seenAccessoryIds.has(product.id)) {
          return false;
        }
        seenAccessoryIds.add(product.id);
        return true;
      })
      .map((product) => ({
        ...product,
        added: !!offerLinesIdLookup.current.find(
          (line) => line.variant.product.id === product.id
        ),
        quantity:
          offerLinesIdLookup.current.find(
            (line) => line.variant.product.id === product.id
          )?.quantity ?? 0,
        selectedVariantId: offerLinesIdLookup.current.find(
          (line) => line.variant.product.id === product.id
        )?.variant.id,
      }));
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    onIsLoading?.(loading);
  }, [loading, onIsLoading]);
  const getOfferLinesFromProductIds = useCallback(
    (
      productIds: string[],
      options?: {
        quantities?: number[];
        update?: boolean;
      }
    ) => {
      const { quantities, update } = options ?? {};
      return productIds.map((productId: string, mainIndex) => {
        const selectedProduct = products.find(
          (product) => product.id === productId
        );
        const lineId = offerLinesIdLookup.current.find(
          (line) => line.variant.product.id === productId
        )?.id;
        if (selectedProduct === undefined) {
          throw new Error(
            `Product with id ${productId} not found in product list`
          );
        }
        const isOffTheShelf = selectedProduct?.type === "OFF_THE_SHELF";
        // Off-the-shelf accessories must have at least 1; honor the
        // product-level minQuantity when no explicit quantity is provided.
        const defaultQuantity = Math.max(selectedProduct.minQuantity ?? 1, 1);
        return {
          ...(update && { id: lineId }),
          variantId: selectedProduct.defaultVariantId,
          ...(isOffTheShelf && {
            quantity: quantities?.[mainIndex] ?? defaultQuantity,
          }),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products]
  );
  const handleBundleChange = useCallback(
    async (value: string) => {
      if (!cognitoToken) {
        return;
      }
      bundleIdCache.current = value;
      currentBundle.current = bundles.findIndex(
        (bundle) => bundle.id === value
      );
      setLoading(true);
      const prevBundle =
        previousBundle.current === -1
          ? undefined
          : bundles[previousBundle.current];
      const newBundle = bundles[currentBundle.current];
      // Auto-on products are the bundle's productIds plus (under the new
      // path) any required accessories. Compatible accessories stay
      // user-driven.
      const autoOnIdsFor = (bundle: EditOfferBundle) =>
        spanAsAccessoryEnabled
          ? Array.from(
              new Set([
                ...bundle.productIds,
                ...(bundle.requiredAccessories ?? []),
              ])
            )
          : [...bundle.productIds];
      const prevAutoOnIds = prevBundle ? autoOnIdsFor(prevBundle) : [];
      const newAutoOnIds = autoOnIdsFor(newBundle);
      const newRequiredAccessoryIds = spanAsAccessoryEnabled
        ? newBundle.requiredAccessories ?? []
        : [];
      const newCompatibleIds = spanAsAccessoryEnabled
        ? newBundle.compatibleAccessories ?? []
        : [];
      const newAvailableIds = new Set([...newAutoOnIds, ...newCompatibleIds]);
      // Remove old auto-on products only when the newly selected bundle no
      // longer allows them. Accessory lines get reset separately below.
      const deleteProductIds = prevAutoOnIds.filter((oldId: string) =>
        spanAsAccessoryEnabled
          ? !newAvailableIds.has(oldId)
          : !newAutoOnIds.includes(oldId)
      );
      if (spanAsAccessoryEnabled) {
        // Bundle changes reset accessory choices. Required accessories for the
        // new bundle are preserved/normalized below because they are auto-on.
        productAccessories.forEach((accessory) => {
          if (
            accessory.added &&
            !newRequiredAccessoryIds.includes(accessory.id) &&
            !deleteProductIds.includes(accessory.id)
          ) {
            deleteProductIds.push(accessory.id);
          }
        });
      } else if (newBundle.id === STORAGE_ADDON_BUNDLE_ID) {
        // Legacy behavior: switching to AOB wipes any added accessories.
        productAccessories.forEach((accessory) => {
          if (accessory.added && !deleteProductIds.includes(accessory.id)) {
            deleteProductIds.push(accessory.id);
          }
        });
      }
      if (newBundle.id === STANDALONE_EV_CHARGER_BUNDLE_ID) {
        // Remove all accessories
        productAccessories.forEach((accessory) => {
          if (accessory.added && !deleteProductIds.includes(accessory.id)) {
            deleteProductIds.push(accessory.id);
          }
        });
      }
      // Under the new path, create missing auto-on products and normalize
      // existing required accessories back to their required minimum.
      const presentProductIds = new Set(
        offerLinesIdLookup.current
          .map((line) => line.variant.product.id)
          .filter((id) => !deleteProductIds.includes(id))
      );
      const candidateCreateIds = newAutoOnIds.filter(
        (newId: string) => !prevAutoOnIds.includes(newId)
      );
      const createProductIds: string[] = [];
      const minQuantityUpdateLines: Array<OfferLineUpdate> = [];
      if (spanAsAccessoryEnabled) {
        newAutoOnIds.forEach((id) => {
          if (!presentProductIds.has(id)) {
            createProductIds.push(id);
            return;
          }
          const existingLine = offerLinesIdLookup.current.find(
            (line) => line.variant.product.id === id
          );
          const product = products.find((p) => p.id === id);
          if (!existingLine || !product || product.type !== "OFF_THE_SHELF") {
            return;
          }
          const minQty = Math.max(product.minQuantity ?? 1, 1);
          const existingQty =
            (existingLine as { quantity?: number }).quantity ?? 0;
          if (newRequiredAccessoryIds.includes(id) && existingQty !== minQty) {
            minQuantityUpdateLines.push({
              id: existingLine.id,
              variantId: existingLine.variant.id,
              quantity: minQty,
            });
          }
        });
      } else {
        // Legacy: create anything in the new bundle's productIds that wasn't
        // in the previous bundle.
        createProductIds.push(...candidateCreateIds);
      }
      if (deleteProductIds.length > 0) {
        const deleteLineIds = offerLinesIdLookup.current
          .filter(
            (line) =>
              deleteProductIds.includes(line.variant.product.id) &&
              line.id !== undefined
          )
          .map((line) => line.id) as string[];
        try {
          const res = await deleteOfferLines(
            cognitoToken,
            offerId,
            deleteLineIds,
            expectedLockVersion.current
          );
          expectedLockVersion.current = res.lockVersion;
          offerLinesIdLookup.current = res.lines;
          previousBundle.current = currentBundle.current;
          setProducts((prevProducts) =>
            prevProducts.map((product) => ({
              ...product,
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 1,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
          setProductAccessories((prevProductAccessories) =>
            prevProductAccessories.map((product) => ({
              ...product,
              added: !!res?.lines.find(
                (line) => line.variant.product.id === product.id
              ),
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 0,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
        } catch (error: any) {
          setOfferLineErrors({
            attemptedAction: "Delete Offer line(s) from bundle selection",
            error,
            hasRetry: true,
          });
          openProductErrorModal();
          setLoading(false);
          return;
        }
      }
      if (minQuantityUpdateLines.length > 0) {
        try {
          const res = await updateOfferLines(
            cognitoToken,
            offerId,
            minQuantityUpdateLines,
            expectedLockVersion.current
          );
          expectedLockVersion.current = res.lockVersion;
          offerLinesIdLookup.current = res.lines;
          previousBundle.current = currentBundle.current;
          setProducts((prevProducts) =>
            prevProducts.map((product) => ({
              ...product,
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 1,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
          setProductAccessories((prevProductAccessories) =>
            prevProductAccessories.map((product) => ({
              ...product,
              added: !!res?.lines.find(
                (line) => line.variant.product.id === product.id
              ),
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 0,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
        } catch (error: any) {
          setOfferLineErrors({
            attemptedAction:
              "Update Offer line(s) from bundle selection (required minimum)",
            error,
            hasRetry: true,
          });
          openProductErrorModal();
          setLoading(false);
          return;
        }
      }
      if (createProductIds.length > 0) {
        const createLines: Array<OfferLineCreate> =
          getOfferLinesFromProductIds(createProductIds);
        try {
          const res = await createOfferLines(
            cognitoToken,
            offerId,
            createLines,
            expectedLockVersion.current
          );
          expectedLockVersion.current = res.lockVersion;
          offerLinesIdLookup.current = res.lines;
          previousBundle.current = currentBundle.current;
          setProducts((prevProducts) =>
            prevProducts.map((product) => ({
              ...product,
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 1,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
          setProductAccessories((prevProductAccessories) =>
            prevProductAccessories.map((product) => ({
              ...product,
              added: !!res?.lines.find(
                (line) => line.variant.product.id === product.id
              ),
              quantity:
                res?.lines.find(
                  (line) => line.variant.product.id === product.id
                )?.quantity ?? 0,
              selectedVariantId: res?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.variant.id,
            }))
          );
        } catch (error: any) {
          setOfferLineErrors({
            attemptedAction: "Create Offer line(s) from bundle selection",
            error,
            hasRetry: true,
          });
          openProductErrorModal();
          setLoading(false);
          return;
        }
      }

      // Ensure previousBundle reflects the new selection even when none of
      // the delete/update/create branches ran. With the new accessory
      // metadata, a switch can be a no-op against the offer (e.g. an
      // optional accessory promoted to required already at the new minimum
      // quantity), and leaving previousBundle stale would make the next
      // switch diff against the wrong index. Only needed under the new
      // path; legacy switches always hit a delete or create branch.
      if (spanAsAccessoryEnabled) {
        previousBundle.current = currentBundle.current;
      }

      setLoading(false);
    },
    [
      cognitoToken,
      bundleIdCache,
      currentBundle,
      bundles,
      previousBundle,
      productAccessories,
      products,
      offerLinesIdLookup,
      offerId,
      expectedLockVersion,
      setOfferLineErrors,
      openProductErrorModal,
      getOfferLinesFromProductIds,
      spanAsAccessoryEnabled,
    ]
  );
  const handleProductVariantChange = useCallback(
    async (productId: string, variantId: string) => {
      if (!cognitoToken) {
        return;
      }
      const lines = getOfferLinesFromProductIds([productId], {
        update: true,
      });
      lines[0].variantId = variantId;
      setLoading(true);
      try {
        const response = await updateOfferLines(
          cognitoToken,
          offerId,
          lines,
          expectedLockVersion.current
        );
        expectedLockVersion.current = response.lockVersion;
        offerLinesIdLookup.current = response.lines;
        setProducts((prevProducts) =>
          prevProducts.map((product) => ({
            ...product,
            quantity:
              response?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.quantity ?? 1,
            selectedVariantId: response?.lines.find(
              (line) => line.variant.product.id === product.id
            )?.variant.id,
          }))
        );
        setProductAccessories((prevProductAccessories) =>
          prevProductAccessories.map((product) => ({
            ...product,
            quantity:
              response?.lines.find(
                (line) => line.variant.product.id === product.id
              )?.quantity ?? 0,
            selectedVariantId: response?.lines.find(
              (line) => line.variant.product.id === product.id
            )?.variant.id,
          }))
        );
      } catch (error: any) {
        console.error(error);
        setOfferLineErrors({
          attemptedAction: "Update Offer line(s) from product variant change",
          error: new Error("Failed to modify offer lines.", { cause: error }),
        });
        openProductErrorModal();
      } finally {
        setLoading(false);
      }
    },
    [
      getOfferLinesFromProductIds,
      expectedLockVersion,
      offerLinesIdLookup,
      offerId,
      cognitoToken,
      openProductErrorModal,
      setOfferLineErrors,
    ]
  );
  const handleAccessoryChange = useCallback(
    async (
      accessoryId: string,
      quantity: number,
      type: "add" | "delete" | "update"
    ) => {
      if (!cognitoToken) {
        return;
      }
      const lineOfferFunction = {
        add: createOfferLines,
        delete: deleteOfferLines,
        update: updateOfferLines,
      };
      setLoading(true);
      let response: EditOffer | undefined;
      if (type === "delete") {
        const deleteLineIds = offerLinesIdLookup.current
          .filter(
            (line) =>
              accessoryId === line.variant.product.id && line?.id !== undefined
          )
          .map((line) => line.id) as string[];
        try {
          response = await lineOfferFunction[type](
            cognitoToken,
            offerId,
            deleteLineIds,
            expectedLockVersion.current
          );
        } catch (error: any) {
          console.error(error);
          setOfferLineErrors({
            attemptedAction: "Delete Offer line(s) from accessory change",
            error,
          });
          openProductErrorModal();
        }
      } else {
        const lines = getOfferLinesFromProductIds([accessoryId], {
          update: type === "update",
          quantities: [quantity],
        });
        try {
          response = await lineOfferFunction[type](
            cognitoToken,
            offerId,
            lines,
            expectedLockVersion.current
          );
        } catch (error: any) {
          console.error(error);
          setOfferLineErrors({
            attemptedAction: "Update Offer line(s) from accessory change",
            error,
          });
          openProductErrorModal();
        }
      }
      if (response !== undefined) {
        expectedLockVersion.current = response.lockVersion;
        offerLinesIdLookup.current = response.lines;
        setProductAccessories((prevAccessories) =>
          prevAccessories.map((accessory) => ({
            ...accessory,
            added: !!response?.lines.find(
              (line) => line.variant.product.id === accessory.id
            ),
            quantity:
              response?.lines.find(
                (line) => line.variant.product.id === accessory.id
              )?.quantity ?? 0,
          }))
        );
      }
      setLoading(false);
    },
    [
      getOfferLinesFromProductIds,
      expectedLockVersion,
      offerLinesIdLookup,
      offerId,
      cognitoToken,
      openProductErrorModal,
      setOfferLineErrors,
    ]
  );
  useEffect(() => {
    if (bundles.length && isOfferRetry.current && bundleIdCache.current) {
      handleBundleChange(bundleIdCache.current);
      isOfferRetry.current = false;
    }
  }, [bundles, bundleIdCache, handleBundleChange, isOfferRetry]);
  // EasterEgg ;)
  const llamaImages = useMemo(
    () => [
      llama1,
      llama2,
      llama3,
      llama4,
      llama5,
      llama6,
      llama7,
      llama8,
      llama9,
      llama10,
      llama11,
      llama12,
    ],
    []
  );
  const [isKonamiLlamas, setIsKonamiLlamas] = useState<boolean>(false);
  const easterEgg = () => {
    setIsKonamiLlamas((isKonamiLlamas) => !isKonamiLlamas);
    console.log("konami llamas activated!");
  };
  useKonami(easterEgg);
  useEffect(() => {
    if (isKonamiLlamas) {
      setBundles((bundles) => {
        return bundles.map((bundle, index) => {
          return Object.assign({}, bundle, { imageUrl: llamaImages[index] });
        });
      });
      setIneligibleBundles((ineligibleBundles) => {
        return ineligibleBundles.map((ineligibleBundle, index) => {
          return Object.assign({}, ineligibleBundle, {
            imageUrl: llamaImages[index],
          });
        });
      });
      setProducts((products) => {
        return products.map((product, index: number) => {
          return Object.assign({}, product, {
            imageUrl: llamaImages[index + bundles.length],
          });
        });
      });
      setProductAccessories((productAccessories) => {
        return productAccessories.map((accessory, index) => {
          return Object.assign({}, accessory, {
            imgUrl: llamaImages[index + bundles.length + products.length],
          });
        });
      });
    }
  }, [isKonamiLlamas, bundles.length, llamaImages, products.length]);
  // End EasterEgg

  const currentBundleProducts =
    bundles[currentBundle.current]?.productIds ?? [];

  const isNonSolar =
    currentBundleProducts.includes(STORAGE_ADDON_PRODUCT_ID) ||
    currentBundleProducts.includes(STANDALONE_PRODUCT_ID) ||
    currentBundleProducts.includes(STANDALONE_EV_CHARGER_PRODUCT_ID);

  const isStorageAddOn = currentBundleProducts.includes(
    STORAGE_ADDON_PRODUCT_ID
  );

  const isStandaloneEvChargerBundle =
    bundles[currentBundle.current]?.id === STANDALONE_EV_CHARGER_BUNDLE_ID;

  const activeBundle = bundles[currentBundle.current];
  const bundleRequiredAccessoryIds = activeBundle?.requiredAccessories ?? [];
  const currentBundleId = activeBundle?.id ?? "";
  const currentBundleProductIds = activeBundle?.productIds ?? [];
  const currentRequiredAccessoryIds = spanAsAccessoryEnabled
    ? bundleRequiredAccessoryIds
    : [];
  const currentCompatibleAccessoryIds =
    activeBundle?.compatibleAccessories ?? [];

  // Products already bundled in productIds (e.g. SPAN on Ultimate Control)
  // are shown under Included Products, not duplicated in accessories.
  const isAccessoryAlsoBundledProduct = (accessoryId: string) =>
    currentBundleProductIds.includes(accessoryId);

  // Force a remount when bundle-backed accessory props change, so
  // ProductAccessory's useState-on-mount values re-initialize from the latest
  // props instead of debouncing a stale add/delete/update on the post-API
  // render. See Copilot review #2 on PR 1555.
  const accessoryKey = (accessory: AdHocAccessoryProduct) =>
    `${currentBundleId}:${accessory.id}:${accessory.added ? "in" : "out"}:${
      accessory.quantity ?? 0
    }`;
  const requiredAccessories = spanAsAccessoryEnabled
    ? productAccessories.filter(
        (accessory) =>
          currentRequiredAccessoryIds.includes(accessory.id) &&
          !isAccessoryAlsoBundledProduct(accessory.id)
      )
    : [];
  const isCompatibleAccessory = (accessory: AdHocAccessoryProduct) => {
    if (spanAsAccessoryEnabled) {
      if (currentRequiredAccessoryIds.includes(accessory.id)) {
        return false;
      }
      if (isAccessoryAlsoBundledProduct(accessory.id)) {
        return false;
      }
      return currentCompatibleAccessoryIds.includes(accessory.id);
    }

    // When sales-exp-span-as-accessory is off (legacy): standalone products
    // are always compatible. Explicit compatibleAccessories IDs are also
    // allowed (e.g. non-standalone).
    if (bundleRequiredAccessoryIds.includes(accessory.id)) {
      return false;
    }
    return (
      accessory.isStandaloneProduct ||
      currentCompatibleAccessoryIds.includes(accessory.id)
    );
  };
  const compatibleAccessories = productAccessories.filter(
    isCompatibleAccessory
  );

  const shouldShowCompatibleAccessories = spanAsAccessoryEnabled
    ? !!compatibleAccessories.length && !isStandaloneEvChargerBundle
    : !!compatibleAccessories.length &&
      !isStorageAddOn &&
      !isStandaloneEvChargerBundle;
  const shouldShowAccessoriesSection = spanAsAccessoryEnabled
    ? !!requiredAccessories.length || shouldShowCompatibleAccessories
    : shouldShowCompatibleAccessories;

  return (
    <section data-testid="product-selection-organism">
      <ProductPageSection>
        <SectionTitle>
          <Typography variant="h4">Bundle Options</Typography>
          <ModalOpener type="button" onClick={onBundleLearnMore}>
            Learn More
          </ModalOpener>
          {ineligibleBundles?.length > 0 && !onlyIneligibleBundles && (
            <SwitchContainer>
              <Switch
                size="large"
                checked={showIneligibleBundles}
                onClick={() => {}}
                onChange={() =>
                  setShowIneligibleBundles(!showIneligibleBundles)
                }
                testId="offer-experience--ProductSelectionOrganism--ShowIneligibleBundles"
              />
              <Typography size="small" fontWeight="medium">
                Show Ineligible Bundles
              </Typography>
            </SwitchContainer>
          )}
        </SectionTitle>
        <SectionContent>
          <SectionContentItemsWrapper>
            {bundles.map((bundleOption) => (
              <RadioGroupLabel key={bundleOption.title}>
                <BundleRadioButton
                  isLoading={loading}
                  label={bundleOption.title}
                  value={bundleOption.id}
                  imageUrl={bundleOption.imageUrl}
                  description={bundleOption.description}
                  bundleId={bundleIdCache.current}
                  handleBundleChange={handleBundleChange}
                  data-testid={"standard-radio"}
                />
              </RadioGroupLabel>
            ))}
            {showIneligibleBundles &&
              ineligibleBundles?.map((bundleOption) => (
                <RadioGroupLabel key={bundleOption.title}>
                  <BundleRadioButton
                    isDisabled={true}
                    disqualifyingReasons={bundleOption.disqualifyingReasons}
                    label={bundleOption.title}
                    value={bundleOption.id}
                    imageUrl={bundleOption.imageUrl}
                    description={bundleOption.description}
                    bundleId={bundleIdCache.current}
                    handleBundleChange={handleBundleChange}
                    data-testid={"standard-radio-ineligible"}
                  />
                </RadioGroupLabel>
              ))}
          </SectionContentItemsWrapper>
        </SectionContent>
      </ProductPageSection>
      {!onlyIneligibleBundles && (
        <>
          <SectionHr />
          <ProductPageSection>
            <SectionTitle>
              <Typography variant="h4">
                {isNonSolar ? "Storage Details" : "Included Products"}
              </Typography>
            </SectionTitle>
            <SectionContent>
              <SectionContentItemsWrapper>
                {products.map((product) => {
                  if (
                    bundles[currentBundle.current]?.productIds.includes(
                      product.id
                    )
                  ) {
                    return (
                      <IncludedProduct
                        disabled={loading}
                        imageUrl={product.imageUrl}
                        key={product.title}
                        onUpdate={handleProductVariantChange}
                        productId={product.id}
                        selectedVariantId={product.selectedVariantId}
                        title={product.title}
                        variants={product.variants}
                      />
                    );
                  }
                  return null;
                })}
              </SectionContentItemsWrapper>
            </SectionContent>
          </ProductPageSection>
          {shouldShowAccessoriesSection && (
            <>
              <SectionHr />
              <ProductPageSection>
                <SectionTitle>
                  <Typography variant="h4">Add Energy Accessories</Typography>
                </SectionTitle>
                <SectionContent>
                  <SectionContentItemsWrapper>
                    {spanAsAccessoryEnabled &&
                      requiredAccessories.map((accessory) => (
                        <ProductAccessory
                          onAccessoryChange={handleAccessoryChange}
                          key={accessoryKey(accessory)}
                          productId={accessory.id}
                          title={accessory.title}
                          imageUrl={accessory.imageUrl}
                          added={!!accessory.added}
                          quantity={accessory.quantity ?? 0}
                          disabled={loading}
                          variants={accessory.variants}
                          selectedVariantId={accessory.selectedVariantId}
                          onVariantChange={handleProductVariantChange}
                          required
                          minQuantity={accessory.minQuantity}
                          maxQuantity={accessory.maxQuantity}
                        />
                      ))}
                    {shouldShowCompatibleAccessories &&
                      compatibleAccessories.map((accessory) => (
                        <ProductAccessory
                          onAccessoryChange={handleAccessoryChange}
                          key={accessoryKey(accessory)}
                          productId={accessory.id}
                          title={accessory.title}
                          imageUrl={accessory.imageUrl}
                          added={!!accessory.added}
                          quantity={accessory.quantity!}
                          disabled={loading}
                          variants={accessory.variants}
                          selectedVariantId={accessory.selectedVariantId}
                          onVariantChange={handleProductVariantChange}
                          minQuantity={accessory.minQuantity}
                          maxQuantity={accessory.maxQuantity}
                        />
                      ))}
                  </SectionContentItemsWrapper>
                </SectionContent>
              </ProductPageSection>
            </>
          )}
        </>
      )}
    </section>
  );
};

const ProductPageSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 80px 0;
`;
const SectionTitle = styled.div`
  width: 20%;
  @media (max-width: 670px) {
    width: 100%;
    padding-bottom: 20px;
  }
  > h4 {
    padding-right: 20px;
    margin: 0;
  }
`;
const SectionContent = styled.div`
  width: 80%;
  @media (max-width: 670px) {
    width: 100%;
  }
`;
const SectionHr = styled.hr`
  border-style: solid;
  border-top-width: 0;
  border-color: ${tokens.OFFWHITE_40};
`;
const SectionContentItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;
const RadioGroupLabel = styled.label`
  width: 233px;
  @media (max-width: 536px) {
    width: 100%;
  }
`;
const ModalOpener = styled(HyperlinkButton)`
  display: block;
  margin-top: 24px;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  margin-top: 29px;
`;

export type { ProductSelectionOrganismProps, ErrorType };
export { ProductSelectionOrganism };
